import {
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreOutlined";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useEffect, useState } from "react";
import { CategoryPickerPanel } from "../../components/CategoryPickerPanel";
import { DatePickerField } from "../../components/DatePickerField";
import { AccountSelect } from "../../components/FinanceSelects";
import { TagPickerPanel } from "../../components/TagPickerPanel";
import { CategoryIcon, CategorySelectOption, money } from "../../lib/format";
import type {
  Account,
  Category,
  Group,
  ImportPreviewRow,
  ImportRowUpdatePayload,
  Member,
  Tag,
  TransactionType,
} from "../../lib/types";
import { currencySymbol, dateToOccurredAt, defaultTransferAccountId, hasDuplicateRow, importRowFilterValue, isEditableRowStatus, isRowUncertain, isSystemRowStatus, isTransactionType, missingCurrencyCode, rowDate, rowPreviewChip, rowType, statusLabel, transactionTypeLabels } from "./importHelpers";
import { displayStatusValue } from "./EntityPreview";

function shortRowDate(value: string) {
  if (!value) return "—";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short" }).format(date);
}

export type ImportRowEditorProps = {
  row: ImportPreviewRow;
  group: Group;
  batchSourceAccountId?: string | null;
  accounts: Account[];
  members: Member[];
  categories: Category[];
  tags: Tag[];
  isUpdating: boolean;
  isBatchApplied: boolean;
  onAddCurrency: (code: string, rowId: string) => void;
  onUpdate: (rowId: string, payload: ImportRowUpdatePayload) => void;
};

export function ImportRowEditor({ row, group, batchSourceAccountId, accounts, members, categories, tags, isUpdating, isBatchApplied, onAddCurrency, onUpdate }: ImportRowEditorProps) {
  const [type, setType] = useState<TransactionType>(() => rowType(row));
  const [date, setDate] = useState(() => rowDate(row));
  const [accountId, setAccountId] = useState(row.account_id || "");
  const [transferAccountId, setTransferAccountId] = useState(row.transfer_account_id || "");
  const [categoryId, setCategoryId] = useState(row.category_id || "");
  const [merchant, setMerchant] = useState(row.merchant || "");
  const [note, setNote] = useState(row.note || "");
  const [status, setStatus] = useState(row.status || "pending");
  const [tagIds, setTagIds] = useState<string[]>(row.tag_ids || []);
  const [tagNames, setTagNames] = useState<string[]>(row.tag_names || []);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setType(rowType(row));
    setDate(rowDate(row));
    setAccountId(row.account_id || "");
    setTransferAccountId(row.transfer_account_id || "");
    setCategoryId(row.category_id || "");
    setMerchant(row.merchant || "");
    setNote(row.note || "");
    setStatus(row.status || "pending");
    setTagIds(row.tag_ids || []);
    setTagNames(row.tag_names || []);
  }, [batchSourceAccountId, row]);

  const selectedCategory = categories.find((category) => category.id === categoryId);
  const selectedTags = tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)).filter((tag): tag is Tag => Boolean(tag));
  // Tags parsed from the import file are only created when the batch is applied, so they have no id
  // yet. Show them as pending chips (skip any the reviewer has already picked as an existing tag).
  const selectedTagNames = new Set(selectedTags.map((tag) => tag.name.toLowerCase()));
  const pendingTagNames = tagNames.filter((name) => !selectedTagNames.has(name.toLowerCase()));
  const uncertain = isRowUncertain(row);
  const amountClass = type === "expense" ? "amount-expense" : type === "income" || type === "correction" ? "amount-income" : "amount-transfer";
  const amountSign = type === "expense" ? "-" : type === "income" || type === "correction" ? "+" : "";
  const normalizedStatus = String(status || "pending");
  const isReadyRow = importRowFilterValue({ ...row, status: normalizedStatus, type, category_id: categoryId }) === "ready";
  const statusValue = isReadyRow && normalizedStatus === "pending" && type !== "transfer" ? "confirmed" : displayStatusValue(normalizedStatus);
  const canShowStatusButtons = isEditableRowStatus(statusValue);
  const isSystemStatus = isSystemRowStatus(normalizedStatus);
  const rowLocked = isUpdating || isSystemStatus || isBatchApplied;
  const canEditStatus = canShowStatusButtons && !rowLocked;
  const chip = rowPreviewChip(row, uncertain);
  const sourceAccountId = accountId || row.account_id || row.source_account_id || batchSourceAccountId || "";
  const accountEmptyLabel = row.source_account_name ? `${row.source_account_name} (new, from file)` : "Select account";
  const categoryEmptyLabel = !categoryId && row.category_name ? `${row.category_name} (new, from file)` : "No category";
  const missingCurrency = missingCurrencyCode(row);
  const canAddMissingCurrency = Boolean(missingCurrency) && !isUpdating && !isBatchApplied;
  const transferAccount = accounts.find((account) => account.id === transferAccountId);
  const summaryText = merchant || note || row.raw_text || "No source text";
  const summaryDetail = [note, row.raw_text].find((value) => value && value !== summaryText) || "";
  const missingCategory = !categoryId && !row.category_entity_id;
  const categoryPillLabel = selectedCategory
    ? selectedCategory.name
    : row.category_name
      ? `${row.category_name} (new)`
      : missingCategory && !rowLocked
        ? "Select category"
        : "No category";

  const update = (payload: ImportRowUpdatePayload) => onUpdate(row.id, payload);

  const dimmed = hasDuplicateRow(row) || normalizedStatus === "ignored";
  const statusPillTone = chip.color === "success" ? "tone-success" : chip.color === "warning" ? "tone-warning" : "tone-muted";
  const showStatusPill = chip.label !== "Ready" && chip.label !== "Review";

  return (
    <Box id={`import-row-${row.id}`} className={`import-preview-row${uncertain ? " uncertain" : ""}${dimmed ? " dimmed" : ""}`}>
      <Box className="import-row-line" onClick={() => setExpanded((value) => !value)}>
        <span className="import-row-date">{shortRowDate(date)}</span>
        <span className="import-row-desc">
          {summaryText}
          {summaryDetail ? <span className="import-row-raw"> · {summaryDetail}</span> : null}
        </span>
        {showStatusPill ? (
          <span className={`import-row-category-pill ${statusPillTone}`}>{chip.label}</span>
        ) : type === "transfer" ? (
          <span className="import-row-category-pill">→ {transferAccount?.name || "Select account"}</span>
        ) : type === "correction" ? (
          <span className="import-row-category-pill">Correction</span>
        ) : rowLocked ? (
          <span className={`import-row-category-pill${selectedCategory ? " selected" : ""}${missingCategory ? " missing" : ""}`}>
            {selectedCategory ? <CategoryIcon icon={selectedCategory.icon} color={selectedCategory.color} /> : null}
            <span>{categoryPillLabel}</span>
          </span>
        ) : (
          <ButtonBase
            className={`import-row-category-pill clickable${selectedCategory ? " selected" : ""}${missingCategory ? " missing" : ""}`}
            type="button"
            aria-label="Change category"
            onClick={(event) => {
              event.stopPropagation();
              setCategoryPickerOpen(true);
            }}
          >
            {selectedCategory ? <CategoryIcon icon={selectedCategory.icon} color={selectedCategory.color} /> : null}
            <span>{categoryPillLabel}</span>
          </ButtonBase>
        )}
        <strong className={amountClass}>
          <span className="amount-sign">{amountSign}</span>
          <span className="amount-currency">{currencySymbol(row.currency)}</span>
          <span className="amount-value">{money(row.amount)}</span>
        </strong>
        <IconButton
          className="records-icon-button import-row-expand"
          type="button"
          aria-label={expanded ? "Collapse row details" : "Expand row details"}
          aria-expanded={expanded}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {expanded ? (
      <Box className="import-row-fields">
          <DatePickerField
            className="import-date-field"
            size="small"
            value={date}
            disabled={rowLocked}
            onChange={(next) => {
              setDate(next);
              update({ occurred_at: dateToOccurredAt(next) });
            }}
          />
          <TextField
            select
            size="small"
            label="Type"
            value={type}
            disabled={rowLocked}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              const nextType = isTransactionType(event.target.value) ? event.target.value : "expense";
              const nextCategoryId = categories.some((category) => category.id === categoryId && category.type === nextType) ? categoryId : "";
              const nextTransferAccountId = nextType === "transfer" ? defaultTransferAccountId(accounts, sourceAccountId, transferAccountId) : "";
              setType(nextType);
              setCategoryId(nextCategoryId);
              setTransferAccountId(nextTransferAccountId);
              update({
                type: nextType,
                category_id: nextType === "transfer" || nextType === "correction" ? null : nextCategoryId || null,
                transfer_account_id: nextType === "transfer" ? nextTransferAccountId || null : null,
              });
            }}
          >
            {Object.entries(transactionTypeLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Merchant"
            value={merchant}
            disabled={rowLocked}
            onChange={(event) => setMerchant(event.target.value)}
            onBlur={() => update({ merchant: merchant.trim() || null })}
          />
          <AccountSelect
            size="small"
            className="import-account-field"
            label={type === "transfer" ? "From account" : type === "correction" ? "Corrected account" : "Account"}
            accounts={accounts.filter((account) => !account.is_archived || account.id === sourceAccountId)}
            members={members}
            emptyLabel={accountEmptyLabel}
            value={sourceAccountId}
            disabled={rowLocked}
            onChange={(next) => {
              if (!next) return;
              setAccountId(next);
              update({ account_id: next });
            }}
          />
          {type === "transfer" ? (
            <AccountSelect
              size="small"
              label="To account"
              accounts={accounts.filter((account) => account.id !== sourceAccountId && (!account.is_archived || account.id === transferAccountId))}
              members={members}
              emptyLabel="Select account"
              value={transferAccountId}
              disabled={rowLocked}
              onChange={(next) => {
                setTransferAccountId(next);
                update({ type: "transfer", category_id: null, transfer_account_id: next || null });
              }}
            />
          ) : type === "correction" ? null : (
            <Box className="import-category-cell">
              <TextField
                select
                size="small"
                className="category-search-select import-category-field"
                label="Category"
                value={categoryId}
                disabled={rowLocked}
                error={!categoryId && !row.category_entity_id}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: {
                    open: false,
                    displayEmpty: true,
                    onOpen: () => setCategoryPickerOpen(true),
                    renderValue: () =>
                      selectedCategory ? <CategorySelectOption category={selectedCategory} indent={false} /> : categoryEmptyLabel,
                  },
                }}
              >
                <MenuItem value="">{categoryEmptyLabel}</MenuItem>
                {selectedCategory ? (
                  <MenuItem value={selectedCategory.id}>
                    <CategorySelectOption category={selectedCategory} />
                  </MenuItem>
                ) : null}
              </TextField>
            </Box>
          )}
          <TextField
            size="small"
            label="Note"
            value={note}
            disabled={rowLocked}
            onChange={(event) => setNote(event.target.value)}
            onBlur={() => update({ note: note.trim() || null })}
          />
          <Box className="import-row-meta-actions">
            {type === "transfer" || type === "correction" ? <span /> : (
              <Box className="import-row-tags">
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    style={{ backgroundColor: tag.color, color: "#fff" }}
                    disabled={rowLocked}
                    onDelete={
                      rowLocked
                        ? undefined
                        : () => {
                            const next = tagIds.filter((tagId) => tagId !== tag.id);
                            setTagIds(next);
                            update({ tag_ids: next });
                          }
                    }
                  />
                ))}
                {pendingTagNames.map((name) => (
                  <Chip
                    key={`pending-${name}`}
                    size="small"
                    variant="outlined"
                    label={name}
                    title="This tag will be created when the import is applied"
                    disabled={rowLocked}
                    onDelete={
                      rowLocked
                        ? undefined
                        : () => {
                            const next = tagNames.filter((tagName) => tagName.toLowerCase() !== name.toLowerCase());
                            setTagNames(next);
                            update({ tag_names: next });
                          }
                    }
                  />
                ))}
                <Chip
                  className="record-add-tag-chip"
                  size="small"
                  variant="outlined"
                  clickable
                  icon={<AddCircleOutlineIcon fontSize="small" />}
                  label="Add tag"
                  disabled={rowLocked}
                  onClick={() => setTagPickerOpen(true)}
                />
              </Box>
            )}
            {canShowStatusButtons ? (
              <ToggleButtonGroup
                className="import-status-toggle"
                size="small"
                exclusive
                value={statusValue}
                disabled={!canEditStatus}
                aria-label="Import row status"
                onChange={(_, value) => {
                  if (!canEditStatus) return;
                  const next = String(value || "");
                  if (!next) return;
                  setStatus(next);
                  update({ status: next });
                }}
              >
                <ToggleButton value="confirmed" aria-label="Confirm" title="Confirm">
                  <Tooltip title="Confirm">
                    <CheckCircleOutlineIcon fontSize="small" />
                  </Tooltip>
                  <span className="import-status-label">Confirm</span>
                </ToggleButton>
                <ToggleButton value="pending" aria-label="Pending" title="Pending">
                  <Tooltip title="Pending">
                    <PendingIcon fontSize="small" />
                  </Tooltip>
                  <span className="import-status-label">Pending</span>
                </ToggleButton>
                <ToggleButton value="ignored" aria-label="Ignore" title="Ignore">
                  <Tooltip title="Ignore">
                    <VisibilityOffIcon fontSize="small" />
                  </Tooltip>
                  <span className="import-status-label">Ignore</span>
                </ToggleButton>
              </ToggleButtonGroup>
            ) : (
              <Chip className="import-system-status" size="small" label={statusLabel(normalizedStatus)} variant="outlined" />
            )}
          </Box>
      </Box>
      ) : null}
      {row.error ? (
        <Box className="import-row-message-block">
          <p className="import-row-message error">{row.error}</p>
          {missingCurrency ? (
            <Button size="small" variant="outlined" type="button" disabled={!canAddMissingCurrency} onClick={() => onAddCurrency(missingCurrency, row.id)}>
              Add {missingCurrency}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {categoryPickerOpen ? (
        <CategoryPickerPanel
          group={group}
          type={type}
          categories={categories}
          selectedCategoryId={categoryId}
          suggestions={!categoryId ? row.category_suggestions || [] : []}
          onApply={(next) => {
            setCategoryId(next);
            update({ type, category_id: next || null, transfer_account_id: null });
          }}
          onClose={() => setCategoryPickerOpen(false)}
          onCancelAll={() => setCategoryPickerOpen(false)}
        />
      ) : null}
      {tagPickerOpen ? (
        <TagPickerPanel
          group={group}
          categoryId={type === "transfer" || type === "correction" ? "" : categoryId}
          selectedTagIds={tagIds}
          onApply={(next) => {
            setTagIds(next);
            update({ tag_ids: next });
          }}
          onClose={() => setTagPickerOpen(false)}
          onCancelAll={() => setTagPickerOpen(false)}
        />
      ) : null}
    </Box>
  );
}
